/**
 * Where a waiting seat's own terminal lives (WI 34335).
 *
 * The Mac CC publishes each seat's host (reportingHost) and tmux session name
 * (tmuxWindow) to RynContextSnapshots. A waiting row links /terminal with those
 * two values; the terminal grid resolves them to the live pane when it gets the
 * host's session list. Pane ids are never published: they change on every cold
 * roll, and a stale one would open the wrong seat.
 */
import type { ContextSnapshot } from '../models/flock-telemetry.models';

/** The part of a context snapshot the link needs. */
export type SeatSessionSnapshot = Pick<
  ContextSnapshot,
  'instanceId' | 'reportingHost' | 'tmuxWindow'
>;

/** A seat's terminal location; host or session is null when the seat has not reported it. */
export interface SeatSessionLink {
  seat: string;
  host: string | null;
  session: string | null;
}

const blankToNull = (value: string | null | undefined): string | null =>
  value?.trim() || null;

export function seatSessionLinks(
  snapshots: readonly SeatSessionSnapshot[] | null | undefined
): ReadonlyMap<string, SeatSessionLink> {
  const links = new Map<string, SeatSessionLink>();
  for (const s of snapshots ?? []) {
    links.set(s.instanceId, {
      seat: s.instanceId,
      host: blankToNull(s.reportingHost),
      session: blankToNull(s.tmuxWindow),
    });
  }
  return links;
}

/** /terminal query params for a link; unknown parts are left out, never sent as "null". */
export function seatSessionQuery(
  link: SeatSessionLink
): Record<string, string> {
  const query: Record<string, string> = { seat: link.seat };
  if (link.host) query['host'] = link.host;
  if (link.session) query['session'] = link.session;
  return query;
}
